import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';

// Phase 1: multi-venue browsing ("academy" internally/on the backend —
// renamed to "venue" here and everywhere else the customer/admin sees it,
// per Phase 1.1). The chosen venue persists for the rest of the browsing
// session — shown in CustomerLayout's header "change venue" link — instead
// of being forced to ask again on every visit to Book/Memberships (that
// earlier behavior made simply navigating away and back re-prompt for a
// venue every single time, which was the actual complaint). There is no
// default venue: even with a single venue in the system the customer picks
// it from the nearby list.
//
// Selection lives in memory only (no localStorage/sessionStorage) — a page
// reload or a logout (see CustomerLayout's handleLogout) clears it, but
// ordinary in-app navigation does not.
const VenueCtx = createContext({
  venues: [],
  loading: true,
  selectedVenueId: null,
  selectedVenue: null,
  selectVenue: () => {},
  clearVenue: () => {},
  needsPicker: false,
});

export const useVenue = () => useContext(VenueCtx);

export const VenueProvider = ({ children }) => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVenueId, setSelectedVenueId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/academies') // backend endpoint name is unchanged — see README
      .then(({ data }) => {
        if (cancelled) return;
        setVenues(data || []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Drop a selection that no longer matches any venue we got back (e.g. it
  // was deactivated).
  useEffect(() => {
    if (loading) return;
    setSelectedVenueId((prev) => (prev && !venues.some((v) => v.id === prev) ? null : prev));
  }, [loading, venues]);

  const selectVenue = (id) => setSelectedVenueId(id);

  const clearVenue = () => setSelectedVenueId(null);

  const selectedVenue = useMemo(
    () => venues.find((v) => v.id === selectedVenueId) || null,
    [venues, selectedVenueId],
  );

  const value = {
    venues,
    loading,
    selectedVenueId,
    selectedVenue,
    selectVenue,
    clearVenue,
    needsPicker: !loading && !selectedVenueId,
  };

  return <VenueCtx.Provider value={value}>{children}</VenueCtx.Provider>;
};
